/**
 * Opportunities API
 * Query and manage opportunities using Trace API entities system
 * Entity types: 'rfp_opportunity' and 'rfp_award'
 */

import { queryEntities, createEntity, Entity, getAPIClient, type EnrichmentOption } from '@tracekit/core';
import { Opportunity, Award } from '../services/dataSourceIntegrations';
import type { BdPriority, PipelineStage } from '../types/opportunityPipeline';

export interface RecommendedKeyword {
  keyword: string;
  explanation: string;
  citations: Array<{
    id?: number;
    source?: string;
    artifact_name?: string;
    artifact_filename?: string;
    artifact_id?: string;
    chunk_id?: string;
    page?: number;
    section?: string;
    content?: string;
    confidence?: number;
    relevance_score?: number;
  }>;
  citation_count: number;
  /** Confidence 0–100 for ranking/display (citation strength + opportunity match). */
  confidence_pct?: number;
}

/** Location or department with count for confidence % and sorting. */
export interface KeywordFilterWithCount {
  name: string;
  count: number;
}

/** Service subcategory from policy pack (Biology §7-16, Contaminated §17-20, Engineering §21-27). */
export interface ServiceKeywordSubcategory {
  id: number;
  title: string;
  keywords: string[];
}

/** Group of service subcategories for UI (e.g. Biology & EIA, Contaminated Sites, Engineering). */
export interface ServiceKeywordGroup {
  id: string;
  label: string;
  subcategories: ServiceKeywordSubcategory[];
}

export interface RecommendedKeywordsResponse {
  keywords: RecommendedKeyword[];
  total_count: number;
  task_id?: string;
  cached?: boolean;
  // Location and Department Filters
  location_keywords?: string[];
  department_names?: string[];
  location_count?: number;
  department_count?: number;
  // Counts for confidence % and sorting (when present, show "X% of opportunities")
  location_keywords_with_count?: KeywordFilterWithCount[];
  department_names_with_count?: KeywordFilterWithCount[];
  total_opportunities_analyzed?: number;
  // Policy-driven service keywords seed (semantic search suggestions)
  service_keywords?: ServiceKeywordSubcategory[];
  service_keywords_count?: number;
  service_keyword_groups?: ServiceKeywordGroup[];
}

/**
 * Build Keystone-specific enrichment options for RAG (departments and keywords to include).
 * Use with UniversalSearch enrichmentOptions or RAG enrichment_keywords.
 * Source: recommended-keywords response (service_keyword_groups, location_keywords, department_names).
 */
export function buildEnrichmentOptionsFromRecommendedKeywords(data: {
  service_keyword_groups?: ServiceKeywordGroup[];
  location_keywords?: string[];
  department_names?: string[];
}): EnrichmentOption[] {
  const options: EnrichmentOption[] = [];
  if (data.service_keyword_groups) {
    for (const group of data.service_keyword_groups) {
      for (const sub of group.subcategories || []) {
        if (sub.keywords?.length) {
          options.push({
            id: `service-${group.id}-${sub.id}`,
            label: sub.title,
            keywords: sub.keywords,
          });
        }
      }
    }
  }
  if (data.location_keywords) {
    for (const loc of data.location_keywords) {
      if (loc?.trim()) {
        options.push({ id: `location-${loc}`, label: loc, keywords: [loc] });
      }
    }
  }
  if (data.department_names) {
    for (const dept of data.department_names) {
      if (dept?.trim()) {
        options.push({ id: `dept-${dept}`, label: dept, keywords: [dept] });
      }
    }
  }
  return options;
}

/**
 * Opportunity entity structure stored in Trace API
 * Entity type: 'rfp_opportunity' or 'rfp_award'
 */
export interface OpportunityEntity extends Entity {
  entity_type: 'rfp_opportunity' | 'rfp_award';
  data: {
    opportunity_id: string;
    source: 'canada_grants' | 'canada_tenders' | 'samgov' | 'worldbank';
    title: string;
    description: string;
    organization: string;
    deadline: string;
    value?: string;
    url: string;
    document_url?: string;
    keywords: string[];
    service_lines: string[];
    status?: 'open' | 'closed' | 'awarded' | 'cancelled';
    saved?: boolean;
    proposal_id?: string; // Link to generated proposal report
    created_at: string;
    updated_at: string;
    
    // RFP Structure V3 (added 2026-01-24)
    rfp_structure?: import('../types/rfp').RfpStructureMetadata;
  };
  
  // RFP Extraction Status Fields (added 2026-01-24)
  rfp_structure_metadata?: import('../types/rfp').RfpStructureMetadata;
  rfp_extraction_status?: import('../types/rfp').RfpExtractionStatus;
  rfp_extraction_method?: import('../types/rfp').RfpExtractionMethod;
  rfp_extraction_quality_score?: number; // 0-100
  rfp_extracted_at?: string;
  rfp_extraction_error?: string;
}

/**
 * Query opportunities using Trace API queryEntities
 * Entity type: 'rfp_opportunity'
 */
export async function queryOpportunities(
  projectId: string,
  filters?: {
    source?: string;
    status?: string;
    service_line?: string;
    saved?: boolean;
    search?: string;
  },
  limit: number = 50,
  offset: number = 0
): Promise<{ opportunities: OpportunityEntity[]; total_count: number }> {
  // Build filters for Trace API entity query
  const entityFilters: Record<string, any> = {};

  // Trace API filters use dot notation for nested JSONB fields
  if (filters?.source) {
    entityFilters['data.source'] = filters.source;
  }
  if (filters?.status) {
    entityFilters['data.status'] = filters.status;
  }
  if (filters?.service_line) {
    // For array contains, use the filter format supported by Trace API
    entityFilters['data.service_lines'] = filters.service_line;
  }
  if (filters?.saved !== undefined) {
    entityFilters['data.saved'] = filters.saved;
  }

  // Query using existing Trace API entity system
  const response = await queryEntities({
    project_id: projectId,
    entity_type: 'rfp_opportunity',
    filters: entityFilters,
    limit,
    offset,
    sort_by: 'created_at',
    order: 'desc',
  });

  let opportunities = response.entities as OpportunityEntity[];

  // Client-side search filtering (could be moved to server-side with text search)
  if (filters?.search) {
    const searchLower = filters.search.toLowerCase();
    opportunities = opportunities.filter(
      (opp) =>
        opp.data?.title?.toLowerCase().includes(searchLower) ||
        opp.data?.description?.toLowerCase().includes(searchLower) ||
        opp.data?.organization?.toLowerCase().includes(searchLower) ||
        opp.data?.keywords?.some((k: string) => k.toLowerCase().includes(searchLower))
    );
  }

  return {
    opportunities,
    total_count: response.total_count || opportunities.length,
  };
}

/**
 * Query awards using Trace API queryEntities
 * Entity type: 'rfp_award'
 */
export async function queryAwards(
  projectId: string,
  filters?: {
    source?: string;
    service_line?: string;
    year?: number;
  },
  limit: number = 50,
  offset: number = 0
): Promise<{ awards: OpportunityEntity[]; total_count: number }> {
  const entityFilters: Record<string, any> = {};

  if (filters?.source) {
    entityFilters['data.source'] = filters.source;
  }
  if (filters?.service_line) {
    entityFilters['data.service_lines'] = filters.service_line;
  }
  if (filters?.year) {
    // Filter by year in deadline
    entityFilters['data.deadline_gte'] = `${filters.year}-01-01`;
    entityFilters['data.deadline_lte'] = `${filters.year}-12-31`;
  }

  const response = await queryEntities({
    project_id: projectId,
    entity_type: 'rfp_award',
    filters: entityFilters,
    limit,
    offset,
    sort_by: 'created_at',
    order: 'desc',
  });

  return {
    awards: response.entities as OpportunityEntity[],
    total_count: response.total_count || 0,
  };
}

/**
 * Save an opportunity (update saved status)
 * Creates a new entity with updated saved flag
 */
export async function saveOpportunity(
  projectId: string,
  opportunityId: string
): Promise<OpportunityEntity> {
  // Query to find the opportunity
  const { opportunities } = await queryOpportunities(projectId, {}, 1000, 0);
  const opportunity = opportunities.find((opp) => opp.data?.opportunity_id === opportunityId);

  if (!opportunity) {
    throw new Error('Opportunity not found');
  }

  // Create new entity with updated saved status
  const updatedData = {
    ...opportunity.data,
    saved: true,
    updated_at: new Date().toISOString(),
  };

  const updatedEntity = await createEntity({
    project_id: projectId,
    entity_type: 'rfp_opportunity',
    data: updatedData,
    source_artifact_id: opportunity.source_artifact_id || undefined,
  });

  return updatedEntity as OpportunityEntity;
}

/**
 * Create opportunity entity from external opportunity data
 * Stores as 'rfp_opportunity' entity type in Trace API
 */
export async function createOpportunityEntity(
  projectId: string,
  opportunity: Opportunity
): Promise<OpportunityEntity> {
  const entity = await createEntity({
    project_id: projectId,
    entity_type: 'rfp_opportunity',
    data: {
      opportunity_id: opportunity.id,
      source: opportunity.source,
      title: opportunity.title,
      description: opportunity.description,
      organization: opportunity.organization,
      deadline: opportunity.deadline,
      value: opportunity.value,
      url: opportunity.url,
      document_url: opportunity.document_url,
      keywords: opportunity.keywords,
      service_lines: opportunity.service_lines,
      status: 'open',
      saved: false,
      created_at: opportunity.created_at,
      updated_at: opportunity.updated_at,
    },
  });

  return entity as OpportunityEntity;
}

/**
 * Mark opportunity as awarded (convert to award entity)
 */
export async function markOpportunityAsAwarded(
  projectId: string,
  opportunityId: string,
  proposalId?: string
): Promise<OpportunityEntity> {
  const { opportunities } = await queryOpportunities(projectId, {}, 1000, 0);
  const opportunity = opportunities.find((opp) => opp.data?.opportunity_id === opportunityId);

  if (!opportunity) {
    throw new Error('Opportunity not found');
  }

  // Create award entity
  const awardData = {
    ...opportunity.data,
    status: 'awarded',
    proposal_id: proposalId,
    updated_at: new Date().toISOString(),
  };

  const awardEntity = await createEntity({
    project_id: projectId,
    entity_type: 'rfp_award',
    data: awardData,
    source_artifact_id: opportunity.source_artifact_id || undefined,
  });

  return awardEntity as OpportunityEntity;
}

/**
 * Create award entity from external award data
 * Stores as 'rfp_award' entity type in Trace API
 */
export async function createAwardEntity(
  projectId: string,
  award: Award
): Promise<OpportunityEntity> {
  const entity = await createEntity({
    project_id: projectId,
    entity_type: 'rfp_award',
    data: {
      award_id: award.id,
      opportunity_id: award.opportunity_id,
      source: award.source,
      title: award.title,
      description: award.description || '',
      organization: award.organization,
      awardee: award.awardee,
      award_date: award.award_date,
      award_value: award.award_value,
      award_number: award.award_number,
      url: award.url,
      created_at: award.created_at,
      updated_at: award.updated_at,
    },
  });

  return entity as OpportunityEntity;
}

/**
 * Get recommended search keywords for opportunities based on company capabilities
 * Uses RAG to analyze company capabilities and generate effective search keywords
 * 
 * NEW ASYNC BEHAVIOR:
 * - Returns immediately if cached
 * - Otherwise returns task_id for polling status
 */
export async function getRecommendedKeywords(
  projectId: string,
  bypassCache: boolean = false
): Promise<RecommendedKeywordsResponse & { task_id?: string; cached?: boolean }> {
  const client = getAPIClient();
  const response = await client.post<RecommendedKeywordsResponse & { task_id?: string; cached?: boolean }>(
    '/api/opportunities/recommended-keywords',
    {},
    {
      params: {
        project_id: projectId,
        bypass_cache: bypassCache,
      },
    }
  );
  return response;
}

export interface WeeklyReviewSummaryOpportunityInput {
  id: string;
  title: string;
  organization?: string;
  deadline?: string;
  match_score?: number;
  weekly_relevance_score?: number;
}

export interface WeeklyReviewSummarySectionInput {
  keyword: string;
  opportunities: WeeklyReviewSummaryOpportunityInput[];
}

export interface WeeklyReviewSummariesRequest {
  project_id: string;
  week_label?: string;
  sections: WeeklyReviewSummarySectionInput[];
}

export interface WeeklyReviewSummariesResponse {
  page_summary: string;
  sections: Array<{ keyword: string; summary: string }>;
  cached?: boolean;
}

/** AI (or server fallback) trend blurbs for Weekly Review keyword sections. */
export async function postWeeklyReviewSummaries(
  body: WeeklyReviewSummariesRequest
): Promise<WeeklyReviewSummariesResponse> {
  const client = getAPIClient();
  return client.post<WeeklyReviewSummariesResponse>(
    '/api/opportunities/weekly-review-summary',
    body
  );
}

export interface KeywordWatchlistResponse {
  project_id: string;
  selected_keywords: string[];
  updated_at: string | null;
  updated_by: string | null;
}

/** Project-scoped Weekly Review watchlist. Empty list means use the recommended catalog. */
export async function getKeywordWatchlist(
  projectId: string
): Promise<KeywordWatchlistResponse> {
  const client = getAPIClient();
  return client.get<KeywordWatchlistResponse>('/api/opportunities/keyword-watchlist', {
    params: { project_id: projectId },
  });
}

export async function putKeywordWatchlist(
  projectId: string,
  selectedKeywords: string[]
): Promise<KeywordWatchlistResponse> {
  const client = getAPIClient();
  return client.put<KeywordWatchlistResponse>('/api/opportunities/keyword-watchlist', {
    project_id: projectId,
    selected_keywords: selectedKeywords,
  });
}

export interface TopPerKeywordRequest {
  project_id: string;
  keywords: string[];
  limit?: number;
  per_keyword_candidates?: number;
  geographic_zone?: string;
}

export interface TopPerKeywordResult {
  keyword: string;
  opportunities: OpportunitySearchQueryResponse['entities'];
  total_candidates: number;
  error?: string;
}

export interface TopPerKeywordResponse {
  results: TopPerKeywordResult[];
  generated_at?: string;
}

/** Server port of pickTopSearchOpportunities. Same shortlist MCP opportunities_top uses. */
export async function postTopPerKeyword(
  body: TopPerKeywordRequest
): Promise<TopPerKeywordResponse> {
  const client = getAPIClient();
  return client.post<TopPerKeywordResponse>('/api/opportunities/top-per-keyword', body);
}

/**
 * Get status of keyword generation task
 */
export interface KeywordTaskStatus {
  status: 'running' | 'completed' | 'failed';
  stage: string;
  message: string;
  progress: number;
  keywords?: RecommendedKeyword[];
  total_count?: number;
  location_keywords?: string[];
  department_names?: string[];
  location_count?: number;
  department_count?: number;
  location_keywords_with_count?: KeywordFilterWithCount[];
  department_names_with_count?: KeywordFilterWithCount[];
  total_opportunities_analyzed?: number;
  service_keywords?: ServiceKeywordSubcategory[];
  service_keywords_count?: number;
  service_keyword_groups?: ServiceKeywordGroup[];
  error?: string;
}

export interface OpportunitySearchSuggestion {
  field: string;
  value: string;
  label: string;
}

export interface OpportunitySearchAutocompleteResponse {
  query: string;
  suggestions: OpportunitySearchSuggestion[];
}

export type OpportunitySearchMode = 'keyword' | 'semantic';

export interface OpportunitySearchQueryResponse {
  query: string;
  total_count: number;
  matched_entity_ids: string[];
  /** Present on responses from the composite/semantic search endpoints (added for search-mode toggle). */
  search_mode?: OpportunitySearchMode;
  /** How many rows the backend scored before ranking/truncating to `limit` (diagnostic only). */
  candidates_scanned?: number;
  /** Semantic search only: how many of the scanned candidates had a usable stored embedding. */
  candidates_with_valid_embedding?: number;
  entities: Array<{
    id: string;
    entity_type: string;
    data: Record<string, any>;
    created_at?: string | null;
    updated_at?: string | null;
    /** Relevance score from the composite keyword scorer or semantic cosine similarity. */
    search_score?: number;
    /** Which match tier produced this result: exact_phrase | all_words | partial | fuzzy | semantic. */
    search_tier?: string;
  }>;
}

export async function getKeywordTaskStatus(
  taskId: string
): Promise<KeywordTaskStatus> {
  const client = getAPIClient();
  const response = await client.get<KeywordTaskStatus>(
    `/api/opportunities/recommended-keywords/status/${taskId}`
  );
  return response;
}

export async function searchOpportunitiesAutocomplete(
  projectId: string,
  q: string,
  field?: string,
  limit: number = 12
): Promise<OpportunitySearchAutocompleteResponse> {
  const client = getAPIClient();
  const response = await client.get<OpportunitySearchAutocompleteResponse>(
    '/api/opportunities/search/autocomplete',
    {
      params: {
        project_id: projectId,
        q,
        field,
        limit,
      },
    }
  );
  return response;
}

export async function searchOpportunitiesByFields(
  projectId: string,
  q: string,
  options?: {
    limit?: number;
    includeEntities?: boolean;
    geographicZone?: string | null;
  }
): Promise<OpportunitySearchQueryResponse> {
  const client = getAPIClient();
  const response = await client.post<OpportunitySearchQueryResponse>(
    '/api/opportunities/search/query',
    {
      project_id: projectId,
      q,
      limit: options?.limit ?? 500,
      include_entities: options?.includeEntities ?? false,
      geographic_zone: options?.geographicZone ?? undefined,
    }
  );
  return response;
}

/**
 * Semantic (AI/RAG) opportunity search - embeds the query and ranks opportunities by
 * cosine similarity against their existing content embeddings. Noticeably slower than
 * `searchOpportunitiesByFields` (one embedding round-trip + similarity scan), so callers
 * should treat this as a background upgrade: show the fast keyword results first, then
 * call this and replace the list when it resolves (see OpportunitiesDashboard progressive search).
 */
export async function searchOpportunitiesSemantic(
  projectId: string,
  q: string,
  options?: {
    limit?: number;
    includeEntities?: boolean;
    geographicZone?: string | null;
  }
): Promise<OpportunitySearchQueryResponse> {
  const client = getAPIClient();
  const response = await client.post<OpportunitySearchQueryResponse>(
    '/api/opportunities/search/semantic',
    {
      project_id: projectId,
      q,
      limit: options?.limit ?? 500,
      include_entities: options?.includeEntities ?? false,
      geographic_zone: options?.geographicZone ?? undefined,
    }
  );
  return response;
}

/**
 * Stream recommended keywords with real-time progress updates
 * Uses Server-Sent Events (SSE) for progressive updates
 */
export interface KeywordStreamCallbacks {
  onStatus?: (data: {
    stage: string;
    message: string;
    elapsed?: number;
    opportunities_count?: number;
    common_terms_count?: number;
  }) => void;
  onKeywords?: (keywords: RecommendedKeyword[]) => void;
  onComplete?: (data: {
    keywords: RecommendedKeyword[];
    total_count: number;
    elapsed: number;
    opportunities_analyzed?: number;
    common_terms_found?: number;
    location_keywords?: string[];
    department_names?: string[];
    location_count?: number;
    department_count?: number;
    location_keywords_with_count?: KeywordFilterWithCount[];
    department_names_with_count?: KeywordFilterWithCount[];
    total_opportunities_analyzed?: number;
    service_keywords?: ServiceKeywordSubcategory[];
    service_keywords_count?: number;
    service_keyword_groups?: ServiceKeywordGroup[];
    cached?: boolean;
  }) => void;
  onError?: (error: Error) => void;
}

export async function streamRecommendedKeywords(
  projectId: string,
  bypassCache: boolean = false,
  callbacks: KeywordStreamCallbacks
): Promise<() => void> {
  const { getAPIBaseUrl, getAuthHeaders } = await import('@tracekit/core');
  const baseUrl = getAPIBaseUrl();
  const authHeaders = getAuthHeaders();
  
  let aborted = false;
  let completed = false; // true after onComplete so we don't overwrite with onError when stream is aborted
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  
  const abort = () => {
    aborted = true;
    if (reader) {
      reader.cancel();
      reader = null;
    }
  };
  
  // Start streaming in background
  (async () => {
    try {
      const url = `${baseUrl}/api/opportunities/recommended-keywords/stream?project_id=${encodeURIComponent(projectId)}&bypass_cache=${bypassCache}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...authHeaders,
        },
        body: JSON.stringify({}),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';
      
      if (!reader) {
        throw new Error('Response body is not readable');
      }
      
      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.trim() === '') continue; // Skip empty lines
          
          if (line.startsWith('event: ')) {
            currentEventType = line.substring(7).trim();
            continue;
          }
          
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              
              if (currentEventType === 'status') {
                callbacks.onStatus?.(data);
              } else if (currentEventType === 'keywords') {
                callbacks.onKeywords?.(data.keywords || []);
              } else if (currentEventType === 'complete' || (currentEventType === 'progress' && data?.stage === 'complete')) {
                // complete event, or progress with stage complete (fallback so we never miss location/department data)
                completed = true;
                if (data.location_keywords || data.department_names) {
                  console.log(
                    `[Keywords] Received ${data.location_count || 0} locations, ${data.department_count || 0} departments`
                  );
                }
                callbacks.onComplete?.(data);
                abort();
                break;
              } else if (currentEventType === 'progress') {
                callbacks.onStatus?.(data);
              } else if (currentEventType === 'error') {
                callbacks.onError?.(new Error(data.message || 'Unknown error occurred'));
                abort();
                break;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, line);
            }
            currentEventType = ''; // Reset after processing
          }
        }
      }
    } catch (error: any) {
      if (!aborted && !completed) {
        callbacks.onError?.(new Error(error?.message || 'Stream error occurred'));
      }
    } finally {
      if (reader) {
        reader.releaseLock();
        reader = null;
      }
    }
  })();
  
  return abort;
}

/**
 * Stream RFP document processing job progress
 * Uses Server-Sent Events (SSE) for real-time updates
 */
export interface RFPJobStreamCallbacks {
  onStatus?: (data: {
    stage: string;
    message: string;
    detail?: string | null;
    progress: number;
    job_status: string;
    job_type: string;
    elapsed: number;
  }) => void;
  onComplete?: (data: {
    success: boolean;
    message: string;
    elapsed: number;
    job_id: string;
  }) => void;
  onError?: (error: Error) => void;
}

export async function streamRFPJobProgress(
  opportunityId: string,
  jobId: string,
  projectId: string,
  callbacks: RFPJobStreamCallbacks
): Promise<() => void> {
  const { getAPIBaseUrl, getAuthHeaders } = await import('@tracekit/core');
  const baseUrl = getAPIBaseUrl();
  const authHeaders = getAuthHeaders();
  
  let aborted = false;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  
  const abort = () => {
    aborted = true;
    if (reader) {
      reader.cancel();
      reader = null;
    }
  };
  
  // Start streaming in background
  (async () => {
    try {
      const url = `${baseUrl}/api/opportunities/${opportunityId}/rfp-documents/jobs/${jobId}/stream?project_id=${encodeURIComponent(projectId)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          ...authHeaders,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      if (!response.body) {
        throw new Error('No response body received');
      }
      
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';
      
      let completedCleanly = false;
      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          const raw = line.replace(/\r$/, '');
          if (raw.trim() === '') continue;
          
          if (raw.startsWith('event: ')) {
            currentEventType = raw.substring(7).trim();
            continue;
          }
          
          if (raw.startsWith('data: ')) {
            try {
              const data = JSON.parse(raw.substring(6));
              
              if (currentEventType === 'status') {
                // Log all status updates for debugging
                console.log('[RFP Job Stream] Status update:', data);
                callbacks.onStatus?.(data);
                // Some backends emit terminal state only on `status` (never send `event: complete`), which left the UI streaming forever.
                const st = String(data.stage || '').toLowerCase();
                const js = String(data.job_status || '').toLowerCase();
                if (
                  st === 'complete' ||
                  st === 'completed' ||
                  ['success', 'completed', 'succeeded', 'successful'].includes(js)
                ) {
                  completedCleanly = true;
                  callbacks.onComplete?.({
                    success: true,
                    message: data.message || 'Job completed',
                    elapsed: typeof data.elapsed === 'number' ? data.elapsed : 0,
                    job_id: jobId,
                  });
                  abort();
                }
              } else if (currentEventType === 'complete') {
                completedCleanly = true;
                callbacks.onComplete?.(data);
                abort();
                break;
              } else if (currentEventType === 'error') {
                completedCleanly = true;
                callbacks.onError?.(new Error(data.message || 'Unknown error occurred'));
                abort();
                break;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, raw);
            }
            currentEventType = '';
          }
        }
      }
      // If the server closed the stream without sending a complete/error event and the
      // client didn't abort, signal a disconnection so the hook can trigger reconnection.
      if (!aborted && !completedCleanly) {
        callbacks.onError?.(new Error('Stream closed unexpectedly — will reconnect'));
      }
    } catch (error: any) {
      if (!aborted) {
        callbacks.onError?.(new Error(error?.message || 'Stream error occurred'));
      }
    } finally {
      if (reader) {
        reader.releaseLock();
        reader = null;
      }
    }
  })();
  
  return abort;
}

/**
 * V3 RFP Structure Extraction API Methods
 */

/**
 * Trigger V3 RFP structure extraction for an opportunity
 * @param opportunityId - Opportunity UUID
 * @param projectId - Project UUID
 * @param artifactIds - Optional list of artifact IDs to extract from
 * @param forceReExtract - Force re-extraction even if recent extraction exists
 */
export async function extractOpportunityStructureV3(
  opportunityId: string,
  projectId: string,
  artifactIds?: string[],
  forceReExtract: boolean = false
): Promise<{
  status: string;
  extraction_id: string;
  opportunity_id: string;
  quality_score: number;
  extraction_method: string;
  message: string;
}> {
  const client = getAPIClient();
  const response = await client.post(
    `/api/rfp/opportunities/${opportunityId}/extract`,
    {
      artifact_ids: artifactIds,
      force_re_extract: forceReExtract,
    },
    {
      params: { project_id: projectId },
    }
  );
  
  if (!response || !response.data) {
    console.error('[extractOpportunityStructureV3] Empty response from API:', response);
    throw new Error('Empty response from extraction API');
  }
  
  return response.data;
}

/**
 * Get the extracted RFP structure for an opportunity
 * @param opportunityId - Opportunity UUID
 * @param projectId - Project UUID
 */
export async function getOpportunityStructureV3(
  opportunityId: string,
  projectId: string
): Promise<{
  opportunity_id: string;
  structure: import('../types/rfp').RfpStructureMetadata | null;
  extraction_status: import('../types/rfp').RfpExtractionStatus;
  quality_score: number | null;
  extracted_at: string | null;
}> {
  const client = getAPIClient();
  const response = await client.get(
    `/api/rfp/opportunities/${opportunityId}/structure`,
    {
      params: { project_id: projectId },
    }
  );
  
  if (!response || !response.data) {
    console.error('[getOpportunityStructureV3] Empty response from API:', response);
    throw new Error('Empty response from structure API');
  }
  
  return response.data;
}

/**
 * Get the extraction status for an opportunity
 * @param opportunityId - Opportunity UUID
 * @param projectId - Project UUID
 */
export async function getOpportunityExtractionStatusV3(
  opportunityId: string,
  projectId: string
): Promise<{
  opportunity_id?: string;
  /** DB path uses `status`; Redis path may also set `extraction_status`. */
  status?: import('../types/rfp').RfpExtractionStatus | string;
  extraction_status?: import('../types/rfp').RfpExtractionStatus | string;
  extraction_id?: string;
  extraction_method: string | null;
  quality_score: number | null;
  extracted_at: string | null;
  error_message?: string | null;
  error?: string | null;
  can_re_extract?: boolean;
  next_extraction_available_at?: string | null;
  sections_count?: number;
  requirements_count?: number;
  criteria_count?: number;
  structure?: any; // Full structure data from Redis
}> {
  const client = getAPIClient();
  const response = await client.get(
    `/api/rfp/opportunities/${opportunityId}/extraction-status`,
    {
      params: { project_id: projectId },
    }
  );
  
  // Response validation: Check if response exists and has data
  if (!response) {
    console.error('[getOpportunityExtractionStatusV3] No response from API');
    throw new Error('No response from extraction status API');
  }
  
  // API client might return data directly or wrapped in .data
  const data = response.data || response;
  
  if (!data || typeof data !== 'object') {
    console.error('[getOpportunityExtractionStatusV3] Invalid response data:', data);
    throw new Error('Invalid response from extraction status API');
  }
  
  // Log for debugging
  console.log('[getOpportunityExtractionStatusV3] ✅ Status received:', {
    extraction_id: data.extraction_id,
    status: data.status || data.extraction_status,
    sections: data.sections_count,
    has_structure: !!data.structure
  });
  
  return data;
}

/**
 * Generate proposal template from approved RFP structure
 * Phase 2: Uses extracted structure + artifact chunks to generate comprehensive template with questions
 */
export async function generateTemplateFromRFPStructure(
  opportunityId: string,
  projectId: string,
  artifactIds: string[]
): Promise<any> {
  const client = getAPIClient();
  console.log('[generateTemplateFromRFPStructure] Starting Phase 2 template generation:', {
    opportunityId,
    artifactIds: artifactIds.length
  });
  
  const response = await client.post(
    `/api/opportunities/${opportunityId}/proposal-template/from-rfp`,
    {
      artifact_ids: artifactIds,
      adapt_questions: true
    },
    {
      params: { project_id: projectId },
      timeout: 120000 // 2 minutes for comprehensive generation
    }
  );
  
  if (!response || !response.data) {
    console.error('[generateTemplateFromRFPStructure] Invalid response');
    throw new Error('Invalid response from template generation API');
  }
  
  console.log('[generateTemplateFromRFPStructure] ✅ Template generated:', {
    sections: response.data.sections?.length || 0,
    questions: response.data.default_questions?.length || 0,
    source: response.data.template_source
  });
  
  return response.data;
}

export interface PipelineStageTransitionResponse {
  entity_id: string;
  pipeline_stage: PipelineStage;
  previous_stage?: PipelineStage;
  title?: string;
}

export interface BdPriorityUpdateResponse {
  entity_id: string;
  bd_priority?: BdPriority | null;
  title?: string;
}

/** SPARC-002: admin-only pipeline stage transition (PATCH /api/opportunities/{id}/pipeline-stage). */
export async function updateOpportunityPipelineStage(
  entityId: string,
  projectId: string,
  pipelineStage: PipelineStage,
): Promise<PipelineStageTransitionResponse> {
  const client = getAPIClient();
  return client.patch<PipelineStageTransitionResponse>(
    `/api/opportunities/${entityId}/pipeline-stage`,
    { pipeline_stage: pipelineStage, project_id: projectId },
  );
}

/** SPARC-002: admin-only BD priority override (PATCH /api/opportunities/{id}/bd-priority). */
export async function updateOpportunityBdPriority(
  entityId: string,
  projectId: string,
  bdPriority: BdPriority | null,
): Promise<BdPriorityUpdateResponse> {
  const client = getAPIClient();
  return client.patch<BdPriorityUpdateResponse>(
    `/api/opportunities/${entityId}/bd-priority`,
    { bd_priority: bdPriority, project_id: projectId },
  );
}
